import { trackDisclaimerModalOpen } from '../../../analytics/event-tracking/trackDisclaimerModalOpen';
import { ContentBrick } from '../../../app-kit/components/ContentBrick/ContentBrick';
import { DisclaimerIcon } from '../../../app-kit/components/DisclaimerIcon/DisclaimerIcon';
import { Alert, AlertVariant, P, paletteHelper, spacingHelper } from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import { usePropertyPageModalContext } from '../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import { PropertyValuationSubBrick } from './subcomponents/PropertyValuationSubBrick/PropertyValuationSubBrick';
import { ValuationType } from './subcomponents/PropertyValuationSubBrick/types';
import { propertyPageValueEstimatesV2Fragment } from '../../graphql/propertyPageQuery';
import { ResultOf } from 'gql.tada';
import { StyledProptrackLogoBrickSource } from '../../../components/ProptrackLogoBrickSource/ProptrackLogoBrickSource';

type PropertyPageValueEstimates = ResultOf<typeof propertyPageValueEstimatesV2Fragment>;
type ValueEstimates = NonNullable<PropertyPageValueEstimates['valueEstimatesV2']>;
type PropertyValuationsEstimates = NonNullable<ValueEstimates['estimates']>;

export interface PropertyValuationBrickProps {
  title: string;
  subtitle?: string;
  estimates: PropertyValuationsEstimates;
  caption?: string;
  hideConfidenceLabel?: boolean;
  alert?: {
    text: string;
    variant: AlertVariant;
  } | null;
}

const PropertyValuationSubBrickContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: ${spacingHelper('extraSmall')};
  margin-bottom: ${spacingHelper('small')};
`;

const Caption = styled(P)`
  padding-top: ${spacingHelper('twoExtraSmall')};
  color: ${paletteHelper('textSecondary')};
`;

const StyledAlert = styled(Alert)`
  margin-bottom: ${spacingHelper('medium')};
`;

export const PropertyValuationBrick: FunctionComponent<PropertyValuationBrickProps> = ({
  title,
  subtitle,
  estimates,
  caption,
  hideConfidenceLabel = false,
  alert,
}) => {
  const { displayModal } = usePropertyPageModalContext();

  const { sale, rental } = estimates;
  if (!sale) return null;

  return (
    <ContentBrick
      title={title}
      subtitle={subtitle || 'Based on comparable sales, past sales and property features'}
      headerChildren={
        <DisclaimerIcon
          title="Open Property Valuation Disclaimer"
          onClick={() => {
            displayModal({ name: 'avmDisclaimer' });
            trackDisclaimerModalOpen({
              inPageSource: 'property_estimate_brick___disclaimer_button',
            });
          }}
        />
      }
    >
      {alert && (
        <StyledAlert id={`avm_alert_${alert.variant}`} variant={alert.variant} sizeVariant="small">
          {alert.text}
        </StyledAlert>
      )}
      <PropertyValuationSubBrickContainer>
        <PropertyValuationSubBrick
          title={sale.title}
          subtitle={sale.subtitle}
          confidence={sale.confidence}
          price={sale.price}
          priceSubtitle={sale.priceSubtitle}
          valuationType={ValuationType.sale}
          range={sale.range}
          hiddenEstimateMessage="Login to see full value estimate"
          hideConfidenceLabel={hideConfidenceLabel}
        />
        {rental && (
          <PropertyValuationSubBrick
            title={rental.title}
            subtitle={rental.subtitle}
            confidence={rental.confidence}
            price={rental.price}
            valuationType={ValuationType.rental}
            range={rental.range}
            emptyEstimateMessage={rental.emptyEstimateMessage}
            hiddenEstimateMessage="Login to see full rental estimate"
            hideConfidenceLabel={hideConfidenceLabel}
          />
        )}
      </PropertyValuationSubBrickContainer>
      {caption && <Caption variant="caption">{caption}</Caption>}
      <StyledProptrackLogoBrickSource />
    </ContentBrick>
  );
};
